import React, { useEffect, useState, useRef } from 'react';
const expertiseAreas = [{
  number: '01',
  title: 'AI Strategy',
  description: 'Developing comprehensive AI integration strategies that align with business objectives and enhance user experiences through intelligent automation and data-driven insights.',
  skills: ['MACHINE LEARNING', 'DATA ANALYSIS', 'AI INTEGRATION', 'AUTOMATION']
}, {
  number: '02',
  title: 'UX Design',
  description: 'Creating intuitive, user-centered experiences through extensive research, testing, and iterative design processes that prioritize usability and accessibility.',
  skills: ['USER RESEARCH', 'WIREFRAMING', 'PROTOTYPING', 'USABILITY TESTING']
}, {
  number: '03',
  title: 'UI Design',
  description: 'Crafting visually stunning interfaces that balance aesthetics with functionality, using modern design principles to create cohesive and engaging digital products.',
  skills: ['VISUAL DESIGN', 'DESIGN SYSTEMS', 'INTERACTION DESIGN', 'TYPOGRAPHY']
}, {
  number: '04',
  title: 'Engineering',
  description: "Currently enrolled in programming courses focusing on Python and agentic AI development. I'm a student of code actively building my technical foundation while applying my design expertise to create more informed and technically feasible solutions.",
  skills: ['CSS', 'HTML', 'JAVASCRIPT', 'PYTHON']
}];
export function ExpertiseSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState<boolean[]>([false, false, false, false]);
  const [visibleSkills, setVisibleSkills] = useState<boolean[][]>([[false, false, false, false], [false, false, false, false], [false, false, false, false], [false, false, false, false]]);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Stagger the main items
          expertiseAreas.forEach((area, index) => {
            setTimeout(() => {
              setVisibleItems(prev => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
              // After main item appears, stagger the skills
              area.skills.forEach((_, skillIndex) => {
                setTimeout(() => {
                  setVisibleSkills(prev => {
                    const newState = prev.map(row => [...row]);
                    newState[index][skillIndex] = true;
                    return newState;
                  });
                }, 400 + skillIndex * 100); // Start 400ms after main item, 100ms between skills
              });
            }, index * 250);
          });
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} id="expertise" className="bg-white py-16 sm:py-20 md:py-28 px-4 sm:px-12 md:px-16 lg:px-24 w-full overflow-hidden" data-id="element-1894">
      <div className="container mx-auto" data-id="element-1895">
        <h2 className="text-3xl sm:text-4xl md:text-5xl mb-8 sm:mb-12 md:mb-20 text-black text-center tracking-wide leading-tight" style={{
        fontFamily: "'Gefika', sans-serif",
        fontWeight: 400,
        letterSpacing: '0.02em',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1) rotateX(0deg)' : 'translateY(50px) scale(0.96) rotateX(5deg)',
        filter: isVisible ? 'blur(0px)' : 'blur(6px)',
        transition: 'opacity 1.2s cubic-bezier(0.19, 1, 0.22, 1), transform 1.2s cubic-bezier(0.19, 1, 0.22, 1), filter 1.2s cubic-bezier(0.19, 1, 0.22, 1)',
        transformOrigin: 'center bottom'
      }} data-id="element-1896">
          Top Skills
        </h2>
        <div className="max-w-6xl mx-auto" data-id="element-1897">
          {expertiseAreas.map((area, index) => <div key={index} style={{
          opacity: visibleItems[index] ? 1 : 0,
          transform: visibleItems[index] ? 'translateY(0) scale(1) rotateX(0deg)' : 'translateY(80px) scale(0.92) rotateX(8deg)',
          filter: visibleItems[index] ? 'blur(0px)' : 'blur(8px)',
          transition: 'opacity 1.1s cubic-bezier(0.19, 1, 0.22, 1), transform 1.1s cubic-bezier(0.19, 1, 0.22, 1), filter 1.1s cubic-bezier(0.19, 1, 0.22, 1)',
          transformOrigin: 'center bottom',
          willChange: 'transform, opacity, filter'
        }} data-id="element-1898">
              {/* Divider line */}
              <div className="border-t border-gray-200 mb-6 sm:mb-8" data-id="element-1899"></div>

              <div className="py-3 sm:py-4 mb-6 sm:mb-8" data-id="element-1900">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between" data-id="element-1901">
                  <div className="w-full md:w-2/3 pr-0 md:pr-8 mb-6 md:mb-0" data-id="element-1902">
                    <div className="flex items-center mb-3 sm:mb-4" data-id="element-1903">
                      <span className="text-[#666666] text-sm sm:text-base tracking-wider mr-4" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    fontWeight: 500
                  }} data-id="element-1904">
                        {area.number}
                      </span>
                      <div className="w-8 h-px bg-gray-300" data-id="element-1905"></div>
                    </div>
                    <h3 className="text-black text-2xl sm:text-3xl md:text-4xl mb-4 transition-all duration-300 leading-normal" style={{
                  fontFamily: "'Gefika', sans-serif",
                  fontWeight: 400
                }} data-id="element-1906">
                      {area.title}
                    </h3>
                    <p className="text-[#666666] leading-relaxed text-base sm:text-lg" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  fontWeight: 400
                }} data-id="element-1907">
                      {area.description}
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 mt-4 md:mt-0 flex items-center" data-id="element-1908">
                    <div className="space-y-2 sm:space-y-3 w-full" data-id="element-1909">
                      {area.skills.map((skill, skillIndex) => <div key={skillIndex} className="text-left md:text-right" style={{
                    opacity: visibleSkills[index][skillIndex] ? 1 : 0,
                    transform: visibleSkills[index][skillIndex] ? 'translateX(0) scale(1)' : 'translateX(30px) scale(0.9)',
                    filter: visibleSkills[index][skillIndex] ? 'blur(0px)' : 'blur(4px)',
                    transition: 'opacity 0.7s cubic-bezier(0.19, 1, 0.22, 1), transform 0.7s cubic-bezier(0.19, 1, 0.22, 1), filter 0.7s cubic-bezier(0.19, 1, 0.22, 1)',
                    willChange: 'transform, opacity, filter'
                  }} data-id="element-1910">
                          <p className="text-sm sm:text-base tracking-wider text-[#666666]" style={{
                      fontFamily: "'Ronzino', sans-serif",
                      fontWeight: 500,
                      letterSpacing: '0.12em'
                    }} data-id="element-1911">
                            {skill}
                          </p>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}